import { ref, reactive, onMounted, onBeforeUnmount } from 'vue'
import usePreference from '@teams/common/hooks/usePreference'
import type {
  AllThemeStylesKeysType,
  AllLayoutStylesKeysType
} from '@teams/common/store/preference/constant'

// 当前是否处于预览模式
const inPreview = ref(false)

export const usePreview = () => {
  const setInPreview = (val = true) => {
    inPreview.value = val
  }
  return reactive({
    inPreview,
    setInPreview
  })
}

// 是否展示 h5 预览页面
const isShow = ref(false)

// 使用 hook: h5 preview 组件
export const useH5Preview = () => {
  const show = () => {
    isShow.value = true
  }
  const hide = () => {
    isShow.value = false
  }
  const props = ref({})
  return reactive({
    show,
    hide,
    isShow,
    props
  })
}

// h5 预览页是否加载完毕
const isLoaded = ref(false)
const iframeRef = ref<HTMLIFrameElement | null>(null)

// 使用 hook: 基于 iframe postMessage 给 h5 预览页发送消息使其更改样式
export const useH5PreviewPreference = ({ init = false } = {}) => {
  const h5Preview = useH5Preview()
  const preference = usePreference({ client: 'h5' })
  const setLoaded = (val = true) => {
    isLoaded.value = val
  }
  const changeCurrentPreference = (
    opt: {
      themes?: Array<{
        key: AllThemeStylesKeysType
        value: string
      }>
      layouts?: Array<{
        key: AllLayoutStylesKeysType
        value: string
      }>
    } = {}
  ) => {
    if (isLoaded.value && opt) {
      if (opt.themes)
        preference.changeCurrentThemeStyle(opt.themes, { bindCss: false })
      if (opt.layouts) preference.changeCurrentLayoutStyle(opt.layouts)
      iframeRef.value?.contentWindow?.postMessage(
        {
          eventName: 'h5PreviewStyleChange',
          data: opt
        },
        '*'
      )
    }
  }
  const createThemeByKey = (
    key: string,
    {
      extendsName,
      update = true,
      change = false
    }: {
      // 继承的官方主题
      extendsName?: string
      // 是否创建后自动更新主题列表
      update?: boolean
      // 是否创建后自动切换至新主题(update 需要为 true 才能生效)
      change?: boolean
    } = {}
  ) => {
    if (isLoaded.value) {
      iframeRef.value?.contentWindow?.postMessage(
        {
          eventName: 'h5PreviewCreateThemeByKey',
          data: { key, extendsName, update, change }
        },
        '*'
      )
    }
  }
  const changeCurrentTheme = (key: string) => {
    if (isLoaded.value) {
      preference.changeCurrentTheme(key, { bindCss: false })
      iframeRef.value?.contentWindow?.postMessage(
        {
          eventName: 'h5PreviewThemeChange',
          data: { key }
        },
        '*'
      )
    }
  }
  const refresh = () => {
    if (isLoaded.value) {
      iframeRef.value?.contentWindow?.postMessage(
        {
          eventName: 'h5PreviewRefresh',
          data: {}
        },
        '*'
      )
    }
  }
  const cropThemeBackgroundByFile = (file: File) => {
    if (isLoaded.value) {
      iframeRef.value?.contentWindow?.postMessage(
        {
          eventName: 'h5PreviewCropThemeBackgroundByFile',
          data: { file }
        },
        '*'
      )
    }
  }
  type AllPreferenceStoreStateKey = keyof typeof preference.store['$state']
  const syncH5PreferenceStore = (
    keys: Array<AllPreferenceStoreStateKey> = [
      'themeList',
      'officialThemeList',
      'customThemeList',
      'themeStylesMap',
      'currentTheme',
      'currentThemeStylesMap',
      'currentLayoutStylesMap'
    ]
  ) => {
    // 同步 h5Preference store 中的配置
    const syncPreference = keys.reduce(
      (pre, cur) => {
        try {
          pre[cur] = JSON.parse(JSON.stringify(preference.store[cur]))
        } catch (e) {
          console.error('无法序列化: ', cur, e)
        }
        return pre
      },
      {} as {
        [key in AllPreferenceStoreStateKey]: Record<string, any>
      }
    )
    if (isLoaded.value) {
      iframeRef.value?.contentWindow?.postMessage(
        {
          eventName: 'h5PreviewSyncH5PreferenceStore',
          data: {
            preference: syncPreference
          }
        },
        '*'
      )
    }
  }

  const onMessage = (e: MessageEvent) => {
    function handleIframeLoaded() {
      try {
        const data: Record<string, any> | void = e.data
        if (data?.eventName === 'h5PreviewLoaded') {
          // h5 已完成加载
          setLoaded()
          // 同步 h5 预览页的 preference 主题列表配置
          syncH5PreferenceStore()
        }
      } catch (err) {
        //
        console.error(e, err)
      }
    }
    function handleThemeBackgroundCroped() {
      try {
        const data: Record<string, any> | void = e.data
        if (data?.eventName === 'h5PreviewThemeBackgroundCroped') {
          // h5 已完成加载
          const picUrl = data?.data?.picUrl
          if (picUrl) {
            // 设置图片
            preference.changeCurrentThemeStyle(
              [{ key: 'group-head-background-img', value: picUrl }],
              { bindCss: false }
            )
          }
        }
      } catch (err) {
        //
        console.error(e, err)
      }
    }
    handleIframeLoaded()
    handleThemeBackgroundCroped()
  }
  if (init) {
    onMounted(() => {
      window.addEventListener('message', onMessage)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('message', onMessage)
      setLoaded(false)
      h5Preview.hide()
    })
  }

  return reactive({
    isLoaded,
    setLoaded,
    changeCurrentPreference,
    iframeRef,
    changeCurrentTheme,
    refresh,
    cropThemeBackgroundByFile,
    createThemeByKey,
    syncH5PreferenceStore
  })
}
